// Bilingual content + small i18n helper for Cloudship
import { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "cloudship_lang";

export const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
});

export const translations = {
  en: {
    nav: {
      services: "Services",
      products: "Products",
      methodology: "Methodology",
      about: "About",
      blog: "Blog",
      contact: "Contact",
      cta: "Book a call",
    },
    hero: {
      overline: "Microsoft Azure Cloud Consulting",
      title_1: "Engineering enterprise-grade",
      title_2: "Azure landing zones",
      title_3: "that actually scale.",
      subtitle:
        "Cloudship helps organisations design, govern and automate their Microsoft Azure platform from first business case to fully operating Enterprise-Scale Landing Zones, Cloud Adoption Framework alignment, Well-Architected reviews through a full automation framework.",
      cta_primary: "Start your cloud journey",
      cta_secondary: "Explore services",
      stat_1_value: "100%",
      stat_1_label: "Azure focus",
      stat_2_value: "CAF",
      stat_2_label: "Aligned delivery",
      stat_3_value: "DE",
      stat_3_label: "Based in Germany",
    },
    products: {
      overline: "Products",
      title: "Accelerate your Azure platform with full ownership and customization.",
      subtitle:
        "Each product is a structured, opinionated starting point, refined through real enterprise deployments and tailored to fit your governance, compliance, and team structure. Not a template. Not a blank canvas.",
      cta: "Talk to us about this product",
      items: [
        {
          tags: ["ESLZ+"],
          title: "Beyond Azure Enterprise-Scale Landing Zones",
          desc:
            "Microsoft’s Enterprise-Scale Landing Zone provides excellent guidance and tooling up to the subscription boundary and is intentionally left to the organisation to define and implement. This product takes it further. Customized governance, networking, policies, and guardrails tailored to your organisation’s specific needs. No half-built platforms. No DIY stitching.",
          points: [
            "Beyond subscription vending: workload-readiness",
            "Bootstrapping Landing Zones aligned to your standards",
            "Delivered as versioned, auditable infrastructure code",
          ],
        },
        {
          tags: ["TERRAFORM", "CICD", "GITHUB", "DEVOPS"],
          title: "Terraform Delivery Framework",
          desc:
            "A Well-Architected Terraform automation framework that acts as the central deployment standard for all your product teams. One opinionated baseline, consistent, reusable, and built for scale across the entire organisation, whether it’s in GitHub, Azure DevOps or GitLab.",
          points: [
            "Terraform modules aligned to Azure WAF pillars",
            "Adopted by product teams without platform expertise",
            "Central knowledge, decentralised delivery",
          ],
        },
      ],
    },
    services: {
      overline: "What we do",
      title: "Built around Microsoft’s reference frameworks.",
      subtitle:
        "Every engagement is grounded in the official Azure frameworks, so what you get is not opinion, but a battle-tested architecture you can defend in any review.",
      items: [
        {
          tag: "Strategy",
          title: "Cloud Strategy & Business Requirements",
          desc:
            "We translate business goals into a measurable cloud strategy, target operating model, cost model, organisational design and a sequenced roadmap.",
          points: ["Business case & TCO modelling", "Target Operating Model", "Cloud governance design"],
        },
        {
          tag: "ESLZ",
          title: "Enterprise-Scale Landing Zones",
          desc:
            "Production-ready Azure Landing Zones implemented as code, identity, networking, security, policy and management at enterprise scale.",
          points: ["Management group hierarchy", "Hub & spoke / vWAN networking", "Policy as code & guardrails"],
        },
        {
          tag: "CAF",
          title: "Cloud Adoption Framework",
          desc:
            "End-to-end alignment with Microsoft CAF, strategy, plan, ready, adopt, govern and manage, adapted to your reality, not a slide deck.",
          points: ["CAF assessments", "Adoption plan & wave planning", "Govern & manage disciplines"],
        },
        {
          tag: "WAF",
          title: "Well-Architected Framework Reviews",
          desc:
            "Independent architecture reviews against the five WAF pillars: reliability, security, cost, operational excellence and performance.",
          points: ["WAF workload reviews", "Remediation backlog", "Risk-prioritised action plan"],
        },
        {
          tag: "DevOps",
          title: "Automation & DevOps Frameworks",
          desc:
            "Everything as code: Bicep, Terraform, GitHub Actions and Azure DevOps pipelines wired into your landing zone from day one.",
          points: ["IaC (Bicep / Terraform)", "CI/CD pipelines", "Platform engineering"],
        },
        {
          tag: "Platform",
          title: "Platform & FinOps Operations",
          desc:
            "Run-mode support: keep the platform compliant, cost-optimised and ready for the next workload wave.",
          points: ["Cost optimisation (FinOps)", "Policy lifecycle", "Continuous compliance"],
        },
      ],
    },
    methodology: {
      overline: "How we work",
      title: "A clear path from idea to production.",
      subtitle:
        "Six structured phases, fully aligned with Microsoft’s Cloud Adoption Framework. No black box, no surprises.",
      steps: [
        { n: "01", title: "Discover", desc: "Stakeholder interviews, current-state assessment, business drivers." },
        { n: "02", title: "Strategy", desc: "Define vision, target operating model and a measurable roadmap." },
        { n: "03", title: "Design", desc: "Architect the landing zone, identity, network and governance model." },
        { n: "04", title: "Build", desc: "Deploy everything as code, repeatable, reviewable, auditable." },
        { n: "05", title: "Adopt", desc: "Migrate or build workloads with WAF reviews and DevOps pipelines." },
        { n: "06", title: "Operate", desc: "Hand-over with run-books and FinOps, or continued operation under a support contract with ongoing governance." },
      ],
    },
    about: {
      overline: "About Cloudship",
      title: "Enterprise Grade Microsoft Azure Solution Architect",
      p1:
        "Cloudship is a boutique Microsoft Azure consultancy based in Germany. Senior-led, hands-on, and fully accountable, from architecture decisions to running production code.",
      bullets: [
        "Single-vendor focus: Microsoft Azure",
        "Frameworks-first: CAF, ESLZ, WAF",
        "Everything delivered as code",
        "Based in Germany, EU data-residency aware",
      ],
    },
    blog: {
      overline: "Insights",
      title: "From the Cloudship blog.",
      subtitle: "Field notes on Azure architecture, landing zones and platform engineering.",
      read_more: "Read article",
      back: "Back to blog",
      all_articles: "All articles",
      reading_time: "min read",
      published: "Published",
    },
    contact: {
      overline: "Get in touch",
      title: "Let’s talk about your Azure platform.",
      subtitle:
        "Tell us where you are today and where you want to go. We usually reply within one business day.",
      form: {
        name: "Your name",
        email: "Work email",
        company: "Company (optional)",
        message: "How can we help?",
        submit: "Send message",
        sending: "Sending…",
        success: "Thanks, your message is on its way. We’ll get back to you shortly.",
        error: "Something went wrong. Please try again or email us directly.",
      },
      info: {
        email_label: "Email",
        phone_label: "Phone",
        location_label: "Location",
        location_value: "Germany",
        hours_label: "Hours",
        hours_value: "Mon – Fri, 09:00 – 18:00 CET",
      },
    },
    footer: {
      tagline: "Microsoft Azure platforms, engineered to last.",
      rights: "All rights reserved.",
      nav_title: "Navigate",
      services_title: "Services",
      contact_title: "Contact",
      legal_note: "Impressum",
      privacy_note: "Privacy",
    },
    consent: {
      message:
        "Cloudship doesn’t use tracking or advertising cookies. The only browser storage we use is to remember your language preference (EN/DE). For details, see our",
      link: "Privacy Policy",
      accept: "Got it",
    },
    impressum: {
      title: "Impressum",
      intro: "Information pursuant to § 5 TMG (German Telemedia Act).",
      provider_heading: "Service provider",
      role_label: "Profession",
      role_value: "Digitalization Consultant",
      vat_label: "VAT ID (Umsatzsteuer-ID)",
      vat_value: "DE364430909",
      address_label: "Address",
      address_value: "Schlosserstraße 12, 47809 Krefeld, Germany",
      name_label: "Name",
      name_value: "Samer Alshreibati",
      phone_label: "Phone",
      phone_value: "+49 157 32521421",
      email_label: "Email",
      email_value: "samer.alshreibati@cloud-ship.de",
      web_label: "Web",
      web_value: "www.cloud-ship.de",
      responsible_heading: "Responsible for the content according to § 55 (2) RStV",
      responsible_value: "Samer Alshreibati, Schlosserstraße 12, 47809 Krefeld, Germany",
      dispute_heading: "EU dispute resolution",
      dispute_body:
        "Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
      source_label: "Source",
      source_value: "https://www.e-recht24.de",
    },
    privacy: {
      title: "Privacy Policy",
      intro: "This privacy policy explains how Samer Alshreibati (Cloudship), a freelance Microsoft Azure consultant based in Krefeld, Germany, handles personal data on the cloud-ship.de website. It is written in accordance with the EU General Data Protection Regulation (GDPR / DSGVO) and the German Federal Data Protection Act (BDSG).",
      glance: {
        heading: "1. Privacy at a glance",
        general_h: "General",
        general_p: "The following notes provide a simple overview of what happens to your personal data when you visit this website. Personal data is any information that can be used to identify you personally. Detailed information can be found further down this page.",
        controller_h: "Who is responsible for data collection on this website?",
        controller_p: "Data processing on this website is carried out by the website operator. Contact details can be found in the Impressum.",
        how_h: "How do we collect your data?",
        how_p: "Some data is collected when you provide it to us, for example through the contact form. Other data is collected automatically by our hosting infrastructure when you visit the site (technical data such as browser type, operating system, time of access).",
        why_h: "What do we use your data for?",
        why_p: "Part of the data is collected to ensure that the website is delivered without errors. Other data is used to respond to your inquiries. We do not use analytics or tracking cookies.",
        rights_h: "What rights do you have regarding your data?",
        rights_p: "You have the right, at any time and free of charge, to obtain information about the origin, recipients and purpose of your stored personal data. You also have the right to request correction, restriction or deletion of this data. You may also lodge a complaint with the competent supervisory authority.",
      },
      controller: {
        heading: "2. Controller",
        body:
          "The controller responsible for data processing on this website is:\nSamer Alshreibati (Freiberufler)\nSchlosserstraße 12, 47809 Krefeld, Germany\nPhone: +49 157 32521421\nEmail: samer.alshreibati@cloud-ship.de",
      },
      general: {
        heading: "3. General notes and mandatory information",
        privacy_h: "Data protection",
        privacy_p: "The operator of this site takes the protection of your personal data very seriously. We treat your personal data confidentially and in accordance with statutory data protection regulations and this privacy policy. Please note that data transmitted over the internet (e.g. via email) may have security gaps. Complete protection of data against access by third parties is not possible.",
        revoke_h: "Right to revoke consent",
        revoke_p: "Many data processing operations are only possible with your express consent. You may revoke a consent already given at any time. An informal email to us is sufficient. The lawfulness of data processing carried out before the revocation remains unaffected.",
        complaint_h: "Right to lodge a complaint with the supervisory authority",
        complaint_p:
          "In the event of breaches of data protection law, the data subject has the right to lodge a complaint with the competent supervisory authority. The competent authority for Cloudship is:\nLandesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen, Kavalleriestraße 2–4, 40213 Düsseldorf",
        portability_h: "Right to data portability",
        portability_p: "You have the right to have data that we process automatically on the basis of your consent or in fulfilment of a contract handed over to you or to a third party in a common, machine-readable format.",
        ssl_h: "SSL / TLS encryption",
        ssl_p: "This site uses SSL/TLS encryption for security reasons and to protect the transmission of confidential content, such as inquiries you send to us as the site operator. You can recognize an encrypted connection by the fact that the browser address line changes from 'http://' to 'https://' and by the lock symbol in your browser line.",
        ads_h: "Objection to promotional emails",
        ads_p: "The use of contact data published as part of the Impressum obligation for the purpose of sending unsolicited advertising and information material is hereby objected to. The operator of this site expressly reserves the right to take legal action in the event of unsolicited advertising being sent, for example via spam emails.",
      },
      collection: {
        heading: "4. Data collection on this website",
        cookies_h: "Cookies",
        cookies_p: "This website does not use tracking or analytics cookies. The only browser storage used is a single localStorage entry that remembers your preferred language (EN/DE). This entry contains no personal data and is stored exclusively on your device.",
        logs_h: "Server log files",
        logs_p:
          "Like virtually all websites, the hosting infrastructure of this site automatically records information that your browser transmits. This may include:\n- Browser type and version\n- Operating system used\n- Referrer URL\n- Host name of the accessing computer\n- Time of the server request\n- IP address\nThis data is not merged with other data sources. The legal basis for the processing is Art. 6 (1) lit. f GDPR (legitimate interest in the technically error-free presentation of this website).",
        form_h: "Contact form",
        form_p: "If you send us inquiries via the contact form, the information you provide (name, email, optional company, message) will be stored to process the inquiry and for any follow-up questions. We do not share this data without your consent. The processing of the data entered into the contact form is based on your consent (Art. 6 (1) lit. a GDPR). You can revoke this consent at any time by sending us an informal email. The lawfulness of the processing operations carried out until the revocation remains unaffected. The data you provide in the contact form remains stored until you ask us to delete it, revoke your consent, or the purpose for storing it no longer applies (e.g. once your inquiry has been processed). Mandatory legal provisions — in particular retention periods — remain unaffected.",
      },
      processors: {
        heading: "5. Service providers and processors",
        intro:
          "To operate this website and process contact requests, the following service providers are used. Data processing agreements are in place where required.",
        items: [
          {
            name: "Resend (email delivery)",
            desc:
              "When you submit the contact form, the contents are sent by email via Resend, Inc. (United States). Standard Contractual Clauses (SCCs) and additional safeguards apply. More information: resend.com.",
          },
          {
            name: "Hosting infrastructure",
            desc:
              "The website and its database are hosted on EU-based infrastructure operated by the website operator. Server log files are stored only as long as necessary for security and operational purposes.",
          },
        ],
      },
      source_label: "Source",
      source_value: "https://www.e-recht24.de",
    },
  },

  de: {
    nav: {
      services: "Leistungen",
      products: "Produkte",
      methodology: "Methodik",
      about: "Über uns",
      blog: "Blog",
      contact: "Kontakt",
      cta: "Termin buchen",
    },
    hero: {
      overline: "Microsoft Azure Cloud Consulting",
      title_1: "Enterprise-taugliche",
      title_2: "Azure Landing Zones",
      title_3: "die wirklich skalieren.",
      subtitle:
        "Cloudship unterstützt Unternehmen beim Design, der Governance und der Automatisierung ihrer Microsoft-Azure-Plattform, vom ersten Business Case bis zur betriebsbereiten Enterprise-Scale Landing Zone, CAF-Ausrichtung und Well-Architected-Reviews über ein vollständiges Automatisierungs-Framework.",
      cta_primary: "Cloud-Reise starten",
      cta_secondary: "Leistungen ansehen",
      stat_1_value: "100%",
      stat_1_label: "Azure-Fokus",
      stat_2_value: "CAF",
      stat_2_label: "Konforme Umsetzung",
      stat_3_value: "DE",
      stat_3_label: "Standort Deutschland",
    },
    products: {
      overline: "Produkte",
      title: "Beschleunigen Sie Ihre Azure-Plattform, mit voller Ownership und Anpassbarkeit.",
      subtitle:
        "Jedes Produkt ist ein strukturierter, meinungsstarker Ausgangspunkt, verfeinert in echten Enterprise-Deployments und zugeschnitten auf Ihre Governance, Compliance und Teamstruktur. Kein Template. Keine leere Leinwand.",
      cta: "Über dieses Produkt sprechen",
      items: [
        {
          tags: ["ESLZ+"],
          title: "Beyond Azure Enterprise-Scale Landing Zones",
          desc:
            "Microsofts Enterprise-Scale Landing Zone bietet exzellente Guidance und Tooling bis zur Subscription-Grenze, die weitere Definition und Umsetzung ist bewusst der Organisation überlassen. Dieses Produkt geht weiter. Maßgeschneiderte Governance, Netzwerk, Policies und Guardrails, exakt auf Ihre Organisation zugeschnitten. Keine halbfertigen Plattformen. Kein DIY-Flickwerk.",
          points: [
            "Über Subscription-Vending hinaus: Workload-Readiness",
            "Bootstrapping von Landing Zones nach Ihren Standards",
            "Geliefert als versionierter, auditierbarer Infrastruktur-Code",
          ],
        },
        {
          tags: ["TERRAFORM", "CICD", "GITHUB", "DEVOPS"],
          title: "Terraform Delivery Framework",
          desc:
            "Ein Well-Architected-Terraform-Automatisierungs-Framework als zentraler Deployment-Standard für alle Produktteams. Eine meinungsstarke Baseline, konsistent, wiederverwendbar und skalierbar über die gesamte Organisation hinweg, ob in GitHub, Azure DevOps oder GitLab.",
          points: [
            "Terraform-Module ausgerichtet an den Azure-WAF-Säulen",
            "Von Produktteams ohne Plattform-Expertise adoptierbar",
            "Zentrales Wissen, dezentrale Lieferung",
          ],
        },
      ],
    },
    services: {
      overline: "Was wir tun",
      title: "Aufgebaut auf Microsofts Referenz-Frameworks.",
      subtitle:
        "Jedes Projekt basiert auf den offiziellen Azure-Frameworks, Sie bekommen keine Meinung, sondern eine belastbare Architektur, die jedem Review standhält.",
      items: [
        {
          tag: "Strategie",
          title: "Cloud-Strategie & Business Requirements",
          desc:
            "Wir übersetzen Geschäftsziele in eine messbare Cloud-Strategie, Target Operating Model, Kostenmodell, Organisationsdesign und priorisierte Roadmap.",
          points: ["Business Case & TCO-Modellierung", "Target Operating Model", "Cloud-Governance-Design"],
        },
        {
          tag: "ESLZ",
          title: "Enterprise-Scale Landing Zones",
          desc:
            "Produktionsreife Azure Landing Zones als Code, Identity, Netzwerk, Security, Policy und Management im Enterprise-Maßstab.",
          points: ["Management-Group-Hierarchie", "Hub & Spoke / vWAN", "Policy as Code"],
        },
        {
          tag: "CAF",
          title: "Cloud Adoption Framework",
          desc:
            "End-to-End-Ausrichtung am Microsoft CAF, Strategy, Plan, Ready, Adopt, Govern, Manage, angepasst an Ihre Realität.",
          points: ["CAF-Assessments", "Adoption Plan & Wave-Planung", "Govern- & Manage-Disziplinen"],
        },
        {
          tag: "WAF",
          title: "Well-Architected Reviews",
          desc:
            "Unabhängige Architektur-Reviews gegen die fünf WAF-Säulen: Reliability, Security, Cost, Operational Excellence, Performance.",
          points: ["WAF-Workload-Reviews", "Remediation-Backlog", "Risikobasierter Maßnahmenplan"],
        },
        {
          tag: "DevOps",
          title: "Automatisierung & DevOps",
          desc:
            "Alles als Code: Bicep, Terraform, GitHub Actions und Azure DevOps Pipelines, ab Tag eins in Ihre Landing Zone integriert.",
          points: ["IaC (Bicep / Terraform)", "CI/CD-Pipelines", "Platform Engineering"],
        },
        {
          tag: "Plattform",
          title: "Plattform- & FinOps-Betrieb",
          desc:
            "Run-Mode-Support: Plattform compliant halten, Kosten optimieren und bereit für die nächste Workload-Welle.",
          points: ["Kostenoptimierung (FinOps)", "Policy-Lifecycle", "Continuous Compliance"],
        },
      ],
    },
    methodology: {
      overline: "Wie wir arbeiten",
      title: "Ein klarer Weg von der Idee bis in den Betrieb.",
      subtitle:
        "Sechs strukturierte Phasen, vollständig am Microsoft Cloud Adoption Framework ausgerichtet. Keine Blackbox, keine Überraschungen.",
      steps: [
        { n: "01", title: "Discover", desc: "Stakeholder-Interviews, Ist-Aufnahme, Business-Treiber." },
        { n: "02", title: "Strategie", desc: "Vision, Target Operating Model und messbare Roadmap definieren." },
        { n: "03", title: "Design", desc: "Landing Zone, Identity-, Netzwerk- und Governance-Modell entwerfen." },
        { n: "04", title: "Build", desc: "Alles als Code deployen, wiederholbar, reviewbar, auditierbar." },
        { n: "05", title: "Adopt", desc: "Workloads migrieren oder neu bauen, mit WAF-Reviews und CI/CD." },
        { n: "06", title: "Operate", desc: "Übergabe mit Runbooks und FinOps, oder fortlaufender Betrieb über einen Support-Vertrag mit kontinuierlicher Governance." },
      ],
    },
    about: {
      overline: "Über Cloudship",
      title: "Enterprise-Grade Microsoft Azure Solution Architect",
      p1:
        "Cloudship ist eine Boutique-Beratung für Microsoft Azure mit Sitz in Deutschland. Senior-geführt, hands-on und voll verantwortlich, von der Architekturentscheidung bis zum laufenden Produktivcode.",
      bullets: [
        "Single-Vendor-Fokus: Microsoft Azure",
        "Frameworks-first: CAF, ESLZ, WAF",
        "Alles als Code geliefert",
        "Standort Deutschland, EU-Datenresidenz",
      ],
    },
    blog: {
      overline: "Insights",
      title: "Aus dem Cloudship Blog.",
      subtitle: "Notizen aus der Praxis zu Azure-Architektur, Landing Zones und Platform Engineering.",
      read_more: "Artikel lesen",
      back: "Zurück zum Blog",
      all_articles: "Alle Artikel",
      reading_time: "Min. Lesezeit",
      published: "Veröffentlicht",
    },
    contact: {
      overline: "Kontakt",
      title: "Sprechen wir über Ihre Azure-Plattform.",
      subtitle:
        "Sagen Sie uns, wo Sie heute stehen und wohin Sie wollen. Wir antworten in der Regel innerhalb eines Werktages.",
      form: {
        name: "Ihr Name",
        email: "Geschäftliche E-Mail",
        company: "Unternehmen (optional)",
        message: "Wie können wir helfen?",
        submit: "Nachricht senden",
        sending: "Wird gesendet…",
        success: "Danke, Ihre Nachricht ist unterwegs. Wir melden uns zeitnah.",
        error: "Da ist etwas schiefgelaufen. Bitte erneut versuchen oder direkt per E-Mail.",
      },
      info: {
        email_label: "E-Mail",
        phone_label: "Telefon",
        location_label: "Standort",
        location_value: "Deutschland",
        hours_label: "Erreichbarkeit",
        hours_value: "Mo – Fr, 09:00 – 18:00 MEZ",
      },
    },
    footer: {
      tagline: "Microsoft-Azure-Plattformen, gebaut für die Dauer.",
      rights: "Alle Rechte vorbehalten.",
      nav_title: "Navigation",
      services_title: "Leistungen",
      contact_title: "Kontakt",
      legal_note: "Impressum",
      privacy_note: "Datenschutz",
    },
    consent: {
      message:
        "Cloudship verwendet keine Tracking- oder Werbe-Cookies. Lediglich Ihre bevorzugte Sprache (EN/DE) wird lokal in Ihrem Browser gespeichert. Details finden Sie in unserer",
      link: "Datenschutzerklärung",
      accept: "Verstanden",
    },
    impressum: {
      title: "Impressum",
      intro: "Angaben gemäß § 5 TMG (Telemediengesetz).",
      provider_heading: "Diensteanbieter",
      role_label: "Beruf",
      role_value: "Digitalization Consultant",
      vat_label: "Umsatzsteuer-ID",
      vat_value: "DE364430909",
      address_label: "Anschrift",
      address_value: "Schlosserstraße 12, 47809 Krefeld",
      name_label: "Name",
      name_value: "Samer Alshreibati",
      phone_label: "Telefon",
      phone_value: "+49 157 32521421",
      email_label: "E-Mail",
      email_value: "samer.alshreibati@cloud-ship.de",
      web_label: "Web",
      web_value: "www.cloud-ship.de",
      responsible_heading: "Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV",
      responsible_value: "Samer Alshreibati, Schlosserstraße 12, 47809 Krefeld",
      dispute_heading: "EU-Streitschlichtung",
      dispute_body:
        "Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
      source_label: "Quelle",
      source_value: "https://www.e-recht24.de",
    },
    privacy: {
      title: "Datenschutzerklärung",
      intro: "Diese Datenschutzerklärung erläutert, wie Samer Alshreibati (Cloudship), freiberuflicher Microsoft-Azure-Berater mit Sitz in Krefeld, personenbezogene Daten auf der Website cloud-ship.de verarbeitet. Sie ist im Einklang mit der EU-Datenschutz-Grundverordnung (DSGVO) und dem Bundesdatenschutzgesetz (BDSG) verfasst.",
      glance: {
        heading: "1. Datenschutz auf einen Blick",
        general_h: "Allgemeine Hinweise",
        general_p: "Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. Ausführliche Informationen finden Sie weiter unten auf dieser Seite.",
        controller_h: "Wer ist verantwortlich für die Datenerfassung auf dieser Website?",
        controller_p: "Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.",
        how_h: "Wie erfassen wir Ihre Daten?",
        how_p: "Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen, etwa über das Kontaktformular. Andere Daten werden automatisch durch die Hosting-Infrastruktur beim Besuch der Website erfasst (z. B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).",
        why_h: "Wofür nutzen wir Ihre Daten?",
        why_p: "Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Weitere Daten werden zur Bearbeitung Ihrer Anfragen genutzt. Es kommen keine Tracking- oder Analyse-Cookies zum Einsatz.",
        rights_h: "Welche Rechte haben Sie bezüglich Ihrer Daten?",
        rights_p: "Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung, Einschränkung oder Löschung dieser Daten zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.",
      },
      controller: {
        heading: "2. Verantwortliche Stelle",
        body:
          "Verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:\nSamer Alshreibati (Freiberufler)\nSchlosserstraße 12, 47809 Krefeld\nTelefon: +49 157 32521421\nE-Mail: samer.alshreibati@cloud-ship.de",
      },
      general: {
        heading: "3. Allgemeine Hinweise und Pflichtinformationen",
        privacy_h: "Datenschutz",
        privacy_p: "Der Betreiber dieser Seite nimmt den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung. Wir weisen darauf hin, dass die Datenübertragung im Internet (z. B. bei der Kommunikation per E-Mail) Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist nicht möglich.",
        revoke_h: "Widerruf Ihrer Einwilligung zur Datenverarbeitung",
        revoke_p: "Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Dazu reicht eine formlose Mitteilung per E-Mail. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf unberührt.",
        complaint_h: "Beschwerderecht bei der zuständigen Aufsichtsbehörde",
        complaint_p:
          "Im Falle datenschutzrechtlicher Verstöße steht der betroffenen Person ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu. Zuständig für Cloudship ist:\nLandesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen, Kavalleriestraße 2–4, 40213 Düsseldorf",
        portability_h: "Recht auf Datenübertragbarkeit",
        portability_p: "Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines Vertrags automatisiert verarbeiten, an sich oder an einen Dritten in einem gängigen, maschinenlesbaren Format aushändigen zu lassen.",
        ssl_h: "SSL- bzw. TLS-Verschlüsselung",
        ssl_p: "Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://“ auf „https://“ wechselt und am Schloss-Symbol in Ihrer Browserzeile.",
        ads_h: "Widerspruch gegen Werbe-Mails",
        ads_p: "Der Nutzung von im Rahmen der Impressumspflicht veröffentlichten Kontaktdaten zur Übersendung von nicht ausdrücklich angeforderter Werbung und Informationsmaterialien wird hiermit widersprochen. Der Betreiber der Seite behält sich ausdrücklich rechtliche Schritte im Falle der unverlangten Zusendung von Werbeinformationen, etwa durch Spam-E-Mails, vor.",
      },
      collection: {
        heading: "4. Datenerfassung auf dieser Website",
        cookies_h: "Cookies",
        cookies_p: "Diese Website verwendet keine Tracking- oder Analyse-Cookies. Lediglich ein einzelner localStorage-Eintrag wird gespeichert, um Ihre bevorzugte Sprache (EN/DE) zu merken. Dieser Eintrag enthält keine personenbezogenen Daten und wird ausschließlich auf Ihrem Endgerät gespeichert.",
        logs_h: "Server-Log-Dateien",
        logs_p:
          "Wie nahezu jede Website erfasst auch die Hosting-Infrastruktur dieser Seite automatisch Informationen, die Ihr Browser übermittelt. Dies können sein:\n- Browsertyp und -version\n- Verwendetes Betriebssystem\n- Referrer-URL\n- Hostname des zugreifenden Rechners\n- Uhrzeit der Serveranfrage\n- IP-Adresse\nEine Zusammenführung dieser Daten mit anderen Datenquellen erfolgt nicht. Rechtsgrundlage der Verarbeitung ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der technisch fehlerfreien Darstellung dieser Website).",
        form_h: "Kontaktformular",
        form_p: "Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben (Name, E-Mail, optional Unternehmen, Nachricht) zur Bearbeitung der Anfrage und für den Fall von Anschlussfragen gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter. Die Verarbeitung der in das Kontaktformular eingegebenen Daten erfolgt auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Sie können diese Einwilligung jederzeit widerrufen. Dazu reicht eine formlose Mitteilung per E-Mail. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitungsvorgänge bleibt vom Widerruf unberührt. Die im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur Löschung auffordern, Ihre Einwilligung widerrufen oder der Zweck für die Datenspeicherung entfällt (z. B. nach abgeschlossener Bearbeitung Ihrer Anfrage). Zwingende gesetzliche Bestimmungen, insbesondere Aufbewahrungsfristen, bleiben unberührt.",
      },
      processors: {
        heading: "5. Dienstleister und Auftragsverarbeiter",
        intro:
          "Für den Betrieb dieser Website und die Bearbeitung von Kontaktanfragen kommen folgende Dienstleister zum Einsatz. Auftragsverarbeitungsverträge bestehen, soweit erforderlich.",
        items: [
          {
            name: "Resend (E-Mail-Versand)",
            desc:
              "Beim Absenden des Kontaktformulars wird der Inhalt per E-Mail über Resend, Inc. (USA) versendet. Es gelten Standardvertragsklauseln (SCC) sowie zusätzliche Schutzmaßnahmen. Weitere Informationen: resend.com.",
          },
          {
            name: "Hosting-Infrastruktur",
            desc:
              "Die Website und ihre Datenbank werden auf EU-basierter Infrastruktur des Websitebetreibers gehostet. Server-Log-Dateien werden nur so lange gespeichert, wie es für Sicherheits- und Betriebszwecke erforderlich ist.",
          },
        ],
      },
      source_label: "Quelle",
      source_value: "https://www.e-recht24.de",
    },
  },
};

function getByPath(obj, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return window.localStorage.getItem(STORAGE_KEY) || "en";
  });

  const setLang = useCallback((next) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {}
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key, fallback) => {
      const val = getByPath(translations[lang], key);
      if (val === undefined) {
        const fb = getByPath(translations.en, key);
        return fb !== undefined ? fb : (fallback ?? key);
      }
      return val;
    },
    [lang]
  );

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
